import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  const hasStartedRef = useRef(false);
  
  useEffect(() => {
    if (!start || hasStartedRef.current) return;
    hasStartedRef.current = true;
    
    let startTime: number | null = null;
    let animationFrame: number;
    
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      }
    };
    
    animationFrame = requestAnimationFrame(step);
    
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [target, duration, start]);
  
  return count;
}

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { 
        if (entry.isIntersecting && !statsVisible) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px' }
    );
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [statsVisible]);

  const clients = useCountUp(1000, 2000, statsVisible);
  const uptime = useCountUp(999, 1500, statsVisible);
  const years = useCountUp(19, 1200, statsVisible);
  const servers = useCountUp(3, 800, statsVisible);

  const whatsappNumber = '56942470111';
  const whatsappMessage = encodeURIComponent('Hola, me interesa conocer más sobre sus servicios de hosting.');

  return (
    <>
      <Helmet>
        <title>Hosting y Servidores VPS en Chile | Alta Seguridad, DNS y Email Profesional</title>
        <meta name="description" content="Servicio de hosting, VPS y servidores dedicados en Chile. Alta velocidad, seguridad avanzada, DNS optimizado y correos profesionales con Gmail y Outlook. Soporte técnico local en Santiago y regiones." />
        <meta name="keywords" content="hosting en chile, hosting en santiago, servidores chile, vps chile, correo profesional, DNS chile, hosting seguro, servidor dedicado chile, hosting para empresas, correo Gmail corporativo, servidor Outlook empresarial, hosting rápido" />
        <link rel="canonical" href="https://www.ehostingchile.com/" />
        <meta property="og:title" content="Hosting y Servidores VPS en Chile | Alta Seguridad, DNS y Email Profesional" />
        <meta property="og:description" content="Servicio de hosting, VPS y servidores dedicados en Chile. Alta velocidad, seguridad avanzada, DNS optimizado y correos profesionales con Gmail y Outlook. Soporte técnico local en Santiago y regiones." />
        <meta property="og:url" content="https://www.ehostingchile.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Hosting y Servidores VPS en Chile",
            "description": "Servicio de hosting, VPS y servidores dedicados en Chile con soporte técnico local",
            "url": "https://www.ehostingchile.com/"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "¿Dónde están ubicados los servidores de EhostingChile?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Contamos con servidores en Chile (Santiago), Estados Unidos (Miami) y Europa (Ámsterdam). Puedes elegir la ubicación que mejor se adapte a tu audiencia para obtener el mejor rendimiento y menor latencia."
                }
              },
              {
                "@type": "Question",
                "name": "¿Qué incluye el soporte técnico 24/7 de EhostingChile?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nuestro equipo está disponible 24/7 en español por chat en vivo, WhatsApp, email y sistema de tickets. Brindamos asistencia completa para migraciones, configuración de correos y solución de problemas técnicos."
                }
              },
              {
                "@type": "Question",
                "name": "¿Puedo migrar mi sitio web actual a EhostingChile sin costo?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sí, realizamos migración gratuita desde tu proveedor actual. Transferimos archivos, bases de datos y configuraciones sin tiempo de inactividad. Nuestro equipo técnico verifica todo después de la migración."
                }
              },
              {
                "@type": "Question",
                "name": "¿Qué es el uptime del 99.9% garantizado por EhostingChile?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Garantizamos que tu sitio web estará disponible el 99.9% del tiempo con respaldo SLA. Nuestra infraestructura redundante, monitoreo constante y respaldos automáticos aseguran máxima disponibilidad para tu negocio."
                }
              },
              {
                "@type": "Question",
                "name": "¿Puedo actualizar mi plan de hosting en cualquier momento?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sí, puedes escalar tu plan de hosting o VPS al instante desde cPanel. El cambio aplica de inmediato y solo pagas la diferencia prorrateada del período contratado, sin costo de migración adicional."
                }
              },
              {
                "@type": "Question",
                "name": "¿Qué métodos de pago aceptan en EhostingChile?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Aceptamos WebPay, tarjetas de crédito y débito (Visa, Mastercard, American Express), transferencia bancaria a bancos chilenos y PayPal. Todos los pagos se procesan con encriptación SSL de grado bancario."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <div>
      {/* Hero Section */}
      <section id="inicio" className="pb-12 sm:pb-20 px-4 sm:px-6 min-h-screen flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50"></div>
        <div className="absolute top-20 right-0 w-72 h-72 bg-blue-100/50 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-100/40 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
                <span className="text-gray-900"><strong>Hosting</strong> Rápido,</span>
                <br />
                <span className="text-gray-900">Confiable y con</span>
                <br />
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Soporte Local</span>
              </h1>

              <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
                En <strong>Ehosting Chile</strong> ofrecemos soluciones completas de <strong>hosting, VPS y servidores dedicados</strong>, soporte local en <strong>Santiago</strong>. Infraestructura de alto rendimiento, conexiones seguras y soporte técnico especializado para empresas y profesionales. Máxima estabilidad y seguridad, configuraciones avanzadas de <strong>firewalls, certificados SSL, protección antimalware y backups automáticos</strong>.
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-4">
                <Link to="/contratar" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap inline-flex cursor-pointer text-sm sm:text-base">
                  <span>Comienza Hoy</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link to="/hosting" className="border-2 border-blue-600 text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 whitespace-nowrap inline-flex cursor-pointer text-sm sm:text-base">
                  Ver Planes
                </Link>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3 pt-1 sm:pt-2">
                {[
                  { icon: 'ri-shield-check-line', label: 'SSL Gratis', color: 'text-green-600' },
                  { icon: 'ri-dashboard-line', label: 'cPanel Incluido', color: 'text-blue-600' },
                  { icon: 'ri-download-cloud-line', label: 'Migración Sin Costo', color: 'text-cyan-600' },
                  { icon: 'ri-time-line', label: 'Soporte 24/7', color: 'text-orange-500' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center space-x-1.5 sm:space-x-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-sm border border-gray-100">
                    <i className={`${b.icon} ${b.color} text-sm sm:text-base`}></i>
                    <span className="text-xs sm:text-sm font-medium text-gray-700">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative hidden sm:block">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl"></div>
              <img
                src="https://readdy.ai/api/search-image?query=modern%20isometric%203D%20illustration%20of%20connected%20servers%20and%20cloud%20infrastructure%20with%20network%20nodes%2C%20floating%20security%20shields%2C%20speed%20icons%2C%20and%20cloud%20elements%2C%20blue%20white%20and%20soft%20cyan%20accent%20colors%2C%20clean%20technological%20style%2C%20simple%20background%20with%20subtle%20dot%20pattern%20and%20light%20blue%20radial%20gradient%2C%20professional%20hosting%20technology%20visualization%20for%20Chilean%20company&width=600&height=600&seq=hero-hosting-illustration-v2&orientation=squarish"
                alt="Infraestructura de Hosting EhostingChile"
                className="relative w-full h-auto rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Counter Bar */}
      <section ref={statsRef} className="py-8 sm:py-12 px-4 sm:px-6 bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-600 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-extrabold mb-1">+{clients.toLocaleString()}</div>
              <div className="text-blue-100 text-xs sm:text-sm font-medium">Clientes Satisfechos</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-extrabold mb-1">{(uptime / 10).toFixed(1)}%</div>
              <div className="text-blue-100 text-xs sm:text-sm font-medium">Uptime Garantizado</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-extrabold mb-1">+{years}</div>
              <div className="text-blue-100 text-xs sm:text-sm font-medium">Años de Experiencia</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-extrabold mb-1">{servers}</div>
              <div className="text-blue-100 text-xs sm:text-sm font-medium">Continentes con Servidores</div>
            </div>
          </div>
        </div>
      </section>

      {/* Por Qué Elegirnos */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-white border border-blue-200 rounded-full px-4 py-2 mb-4">
              <span className="text-blue-700 font-medium text-sm">Ventajas Competitivas</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              ¿Por Qué Elegir EhostingChile?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Más de 19 años ofreciendo la mejor combinación de tecnología, soporte y confiabilidad para tu proyecto web
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {[
              { icon: 'ri-customer-service-2-line', title: 'Soporte 24/7 en Español', description: 'Equipo técnico disponible las 24 horas del día, los 7 días de la semana, hablando tu idioma', color: 'bg-blue-100 text-blue-600' },
              { icon: 'ri-shield-check-line', title: 'Uptime 99.9% Garantizado', description: 'Infraestructura redundante y monitoreo constante para máxima disponibilidad de tu sitio', color: 'bg-green-100 text-green-600' },
              { icon: 'ri-global-line', title: 'Servidores en 3 Continentes', description: 'Datacenter en Chile, Estados Unidos y Europa para mejor rendimiento global', color: 'bg-cyan-100 text-cyan-600' },
              { icon: 'ri-lock-line', title: 'SSL Gratuito Incluido', description: 'Certificado SSL Let\'s Encrypt gratis en todos los planes para seguridad total', color: 'bg-emerald-100 text-emerald-600' },
              { icon: 'ri-dashboard-3-line', title: 'Panel cPanel Intuitivo', description: 'Interfaz de administración fácil de usar con instalador de aplicaciones en un clic', color: 'bg-indigo-100 text-indigo-600' },
              { icon: 'ri-download-cloud-2-line', title: 'Migración Sin Costo', description: 'Transferimos tu sitio web desde tu proveedor actual sin cargo adicional ni tiempo de inactividad', color: 'bg-orange-100 text-orange-600' }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center ${feature.color} rounded-2xl mb-4 sm:mb-6`}>
                  <i className={`${feature.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planes de Hosting */}
      <section id="planes" className="py-14 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-gray-100 border border-gray-300 rounded-full px-4 py-2 mb-4">
              <span className="text-gray-700 font-medium text-sm">Planes y Precios</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Elige el Plan Perfecto para tu Proyecto
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Todos los planes incluyen SSL gratuito, cPanel y soporte 24/7 en español
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-10 sm:mb-12">
            {[
              { name: 'Básico', description: 'Ideal para sitios personales', price: '59.000', popular: false, features: ['2 GB SSD NVMe','Tráfico Ilimitado','5 Cuentas Email','Backups Semanales','SSL Gratuito','cPanel Incluido'] },
              { name: 'Pyme', description: 'Perfecto para negocios', price: '85.000', popular: true, features: ['20 GB SSD NVMe','Tráfico Ilimitado','25 Cuentas Email','Backups Diarios','SSL Gratuito','cPanel Incluido','Soporte Prioritario'] },
              { name: 'Empresarial', description: 'Para proyectos grandes', price: '150.000', popular: false, features: ['50 GB SSD NVMe','Tráfico Ilimitado','50 Cuentas Email','Backups Diarios','SSL Gratuito','cPanel Incluido','IP Dedicada'] },
              { name: 'Avanzado', description: 'Máximo rendimiento', price: '230.000', popular: false, features: ['100 GB SSD NVMe','Tráfico Ilimitado','Emails Ilimitados','Backups Diarios','SSL Gratuito','cPanel Incluido','IP Dedicada','CDN Gratis'] }
            ].map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-6 sm:p-8 relative hover:shadow-2xl transition-all duration-300 ${
                  plan.popular
                    ? 'border-2 border-blue-600 lg:-mt-4 shadow-xl shadow-blue-500/10'
                    : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-1 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                      MÁS POPULAR
                    </span>
                  </div>
                )}
                <div className="text-center mb-5 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-gray-500 text-base sm:text-lg">$</span>
                    <span className="text-3xl sm:text-4xl font-bold text-blue-600">{plan.price}</span>
                  </div>
                  <span className="text-gray-500 text-xs sm:text-sm">+ IVA Anual</span>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start space-x-2 sm:space-x-3">
                      <i className="ri-checkbox-circle-fill text-blue-500 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contratar"
                  className={`w-full py-2.5 sm:py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap block text-center cursor-pointer text-sm sm:text-base ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg hover:-translate-y-0.5'
                      : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Contratar Plan
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/hosting" className="inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 cursor-pointer text-sm sm:text-base">
              <span>Ver Todos los Planes de Hosting</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Migración Gratuita */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div>
              <div className="inline-block bg-blue-100 text-blue-700 rounded-full px-4 py-2 text-xs sm:text-sm font-semibold mb-5 sm:mb-6">
                Migración 100% Gratuita y Sin Downtime
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Cambia a EhostingChile<br />
                <span className="text-blue-600">sin complicaciones</span>
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Nuestro equipo experto se encarga de todo el proceso de migración. Transferimos tus archivos, bases de datos y configuraciones sin que tu sitio deje de funcionar.
              </p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {[
                  { icon: 'ri-file-transfer-line', label: 'Transferencia de archivos' },
                  { icon: 'ri-database-2-line', label: 'Migración de bases de datos' },
                  { icon: 'ri-mail-settings-line', label: 'Configuración de emails' },
                  { icon: 'ri-global-line', label: 'Traspaso de dominios' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3 bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-blue-50 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-blue-600 text-base sm:text-xl`}></i>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-gray-700">{item.label}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/contratar"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all whitespace-nowrap text-sm sm:text-base"
              >
                <i className="ri-arrow-right-circle-line text-lg sm:text-xl"></i>
                Solicitar Migración Gratis
              </Link>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {[
                { step: '01', title: 'Regístrate', desc: 'Crea tu cuenta y elige el plan que mejor se adapte a tus necesidades.', icon: 'ri-user-add-line', color: 'from-blue-500 to-blue-600' },
                { step: '02', title: 'Envíanos los datos', desc: 'Comparte los accesos de tu hosting actual y nosotros hacemos el resto.', icon: 'ri-share-line', color: 'from-cyan-500 to-cyan-600' },
                { step: '03', title: 'Migración en progreso', desc: 'Nuestro equipo transfiere todos tus archivos y configuraciones sin interrupciones.', icon: 'ri-loader-4-line', color: 'from-teal-500 to-teal-600' },
                { step: '04', title: '¡Listo para usar!', desc: 'Tu sitio ya está en EhostingChile. Más rápido, más seguro y con soporte local.', icon: 'ri-checkbox-circle-line', color: 'from-green-500 to-green-600' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 sm:gap-5 bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all">
                  <div className={`w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-br ${item.color} text-white rounded-xl flex-shrink-0 shadow-lg`}>
                    <i className={`${item.icon} text-xl sm:text-2xl`}></i>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-400 mb-0.5">PASO {item.step}</div>
                    <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Servicios VPS Preview */}
      <section id="vps" className="py-14 sm:py-20 px-4 sm:px-6 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              <div className="inline-block bg-blue-500/20 text-blue-300 rounded-full px-4 py-2 text-xs sm:text-sm font-semibold border border-blue-500/30">
                Servidores Virtuales Privados
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                Servidores VPS<br />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Potencia y Control Total</span>
              </h2>
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                Recursos dedicados, acceso root completo y escalabilidad instantánea. Ideal para proyectos que requieren alto rendimiento y configuración personalizada.
              </p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {[
                  { icon: 'ri-cpu-line', text: 'CPU y RAM dedicados' },
                  { icon: 'ri-hard-drive-3-line', text: 'SSD NVMe ultra rápido' },
                  { icon: 'ri-ip-line', text: 'IP dedicada incluida' },
                  { icon: 'ri-camera-line', text: 'Snapshots automáticos' },
                  { icon: 'ri-terminal-box-line', text: 'Acceso root completo' },
                  { icon: 'ri-bar-chart-line', text: 'Escalabilidad inmediata' },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2 sm:space-x-3 bg-white/5 rounded-xl p-2.5 sm:p-3 border border-white/10">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-blue-500/20 rounded-lg flex-shrink-0">
                      <i className={`${benefit.icon} text-blue-400 text-base sm:text-lg`}></i>
                    </div>
                    <span className="text-gray-300 text-xs sm:text-sm font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>
              <Link to="/vps" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all whitespace-nowrap text-sm sm:text-base">
                <span>Ver Planes VPS</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {[
                { name: 'VPS Básico', cpu: '2 vCPU', ram: '4 GB RAM', storage: '80 GB SSD', price: '79.990', badge: '' },
                { name: 'VPS Profesional', cpu: '4 vCPU', ram: '8 GB RAM', storage: '150 GB SSD', price: '110.990', badge: 'Popular' },
                { name: 'VPS Empresarial', cpu: '8 vCPU', ram: '16 GB RAM', storage: '150 GB SSD', price: '149.990', badge: '' },
              ].map((vps, index) => (
                <div
                  key={index}
                  className={`rounded-2xl p-4 sm:p-6 border transition-all duration-300 hover:-translate-y-1 ${
                    vps.badge ? 'bg-blue-600/20 border-blue-500/50 shadow-lg shadow-blue-500/10' : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <h3 className="text-lg sm:text-xl font-bold text-white">{vps.name}</h3>
                        {vps.badge && (
                          <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">{vps.badge}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        {[
                          { icon: 'ri-cpu-line', val: vps.cpu },
                          { icon: 'ri-database-2-line', val: vps.ram },
                          { icon: 'ri-hard-drive-line', val: vps.storage },
                        ].map((spec, si) => (
                          <span key={si} className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-300 bg-white/5 px-2 sm:px-3 py-1 rounded-lg">
                            <i className={`${spec.icon} text-blue-400`}></i>
                            {spec.val}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">${vps.price}<span className="text-xs sm:text-sm text-gray-400 font-normal">/mes</span></div>
                      <Link to="/vps" className="bg-blue-600 hover:bg-blue-500 text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap inline-block cursor-pointer">
                        Configurar
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-white border border-gray-300 rounded-full px-4 py-2 mb-4">
              <span className="text-gray-700 font-medium text-sm">Preguntas Frecuentes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">¿Tienes Dudas?</h2>
            <p className="text-base sm:text-lg text-gray-600">
              Encuentra respuestas a las preguntas más comunes sobre nuestros servicios
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {[
              { question: '¿Dónde están ubicados los servidores?', answer: 'Contamos con servidores en Chile (Santiago), Estados Unidos (Miami) y Europa (Ámsterdam). Puedes elegir la ubicación que mejor se adapte a tu audiencia para obtener el mejor rendimiento.' },
              { question: '¿Qué incluye el soporte técnico 24/7?', answer: 'Nuestro equipo de soporte está disponible las 24 horas del día, los 7 días de la semana, en español. Puedes contactarnos por chat en vivo, WhatsApp, email o sistema de tickets.' },
              { question: '¿Puedo migrar mi sitio web actual sin costo?', answer: 'Sí, ofrecemos migración gratuita desde tu proveedor actual. Nuestro equipo técnico se encarga de transferir todos tus archivos, bases de datos y configuraciones sin tiempo de inactividad.' },
              { question: '¿Qué es el uptime del 99.9% garantizado?', answer: 'Garantizamos que tu sitio web estará disponible el 99.9% del tiempo. Contamos con infraestructura redundante, monitoreo 24/7 y sistemas de respaldo automático.' },
              { question: '¿Puedo actualizar mi plan en cualquier momento?', answer: 'Sí, puedes actualizar tu plan de hosting o VPS en cualquier momento desde tu panel de control. El cambio es inmediato y solo pagas la diferencia prorrateada del período actual.' },
              { question: '¿Qué métodos de pago aceptan?', answer: 'Aceptamos tarjetas de crédito y débito (Visa, Mastercard, American Express), transferencia bancaria y WebPay. Todos los pagos son procesados de forma segura.' }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className="text-sm sm:text-base font-semibold text-gray-900 pr-3 sm:pr-4">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-blue-600 transition-transform duration-300 flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-4 sm:px-6 pb-4 sm:pb-5">
                    <p className="text-gray-600 leading-relaxed text-sm">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contacto" className="py-14 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-700 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">¿Listo para Comenzar?</h2>
            <p className="text-base sm:text-xl text-blue-100">Estamos aquí para ayudarte en cada paso del camino</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 sm:gap-8 mb-8 sm:mb-12">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white/20 rounded-full mb-4 sm:mb-6">
                <i className="ri-customer-service-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Soporte Técnico</h3>
              <p className="text-blue-100 mb-4 sm:mb-6 leading-relaxed text-sm">
                ¿Necesitas ayuda? Nuestro equipo está disponible 24/7 para asistirte
              </p>
              <Link to="/soporte" className="bg-white text-blue-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap inline-block cursor-pointer text-sm sm:text-base">
                Crear Ticket
              </Link>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white/20 rounded-full mb-4 sm:mb-6">
                <i className="ri-mail-send-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Contratar Ahora</h3>
              <p className="text-blue-100 mb-4 sm:mb-6 leading-relaxed text-sm">
                Elige tu plan y comienza hoy mismo con migración gratuita incluida
              </p>
              <Link to="/contratar" className="bg-white text-blue-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap inline-block cursor-pointer text-sm sm:text-base">
                Ver Planes
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20">
              <i className="ri-phone-line text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
              <div className="font-semibold mb-1 text-sm sm:text-base">Teléfono</div>
              <div className="text-blue-100 text-xs sm:text-sm">+56 2 2581 9100</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20">
              <i className="ri-mail-line text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
              <div className="font-semibold mb-1 text-sm sm:text-base">Email</div>
              <div className="text-blue-100 text-xs sm:text-sm">soporte@ehostingchile.com</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20">
              <i className="ri-whatsapp-line text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
              <div className="font-semibold mb-1 text-sm sm:text-base">WhatsApp</div>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="nofollow noreferrer"
                className="text-green-300 hover:text-green-200 text-xs sm:text-sm font-semibold transition-colors"
              >
                Escribir ahora →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}
